/**
 * Gimbals — the control stick overlay.
 *
 * Drawn the way the pilot's own transmitter is laid out, because that is the
 * mental model they already have. Mode 2 by default: throttle and rudder on
 * the left, elevator and aileron on the right.
 *
 * The parser normalises every controller's raw range to -1..1, so nothing here
 * needs to know whether the log came from an RC2 or a decade-old Phantom
 * transmitter.
 */

const MODES = {
  // [leftVertical, leftHorizontal, rightVertical, rightHorizontal]
  1: ['stickElevator', 'stickRudder', 'stickThrottle', 'stickAileron'],
  2: ['stickThrottle', 'stickRudder', 'stickElevator', 'stickAileron'],
};

export class Gimbals {
  constructor(container, flight, mode = 2) {
    this.container = container;
    this.flight = flight;
    this.mode = MODES[mode] ? mode : 2;
    this.render();
  }

  render() {
    const left = this.container.querySelector('#gimbal-left');
    const right = this.container.querySelector('#gimbal-right');

    if (!this.flight.has('stickAileron') && !this.flight.has('stickThrottle')) {
      if (left) left.innerHTML = '';
      if (right) right.innerHTML = '';
      this.available = false;
      return;
    }

    this.available = true;
    if (left) {
      left.innerHTML = `<div class="gimbal">${gimbalSvg('left')}</div><div class="gimbal-caption">left stick</div>`;
    }
    if (right) {
      right.innerHTML = `<div class="gimbal">${gimbalSvg('right')}</div><div class="gimbal-caption">right stick</div>`;
    }

    this.leftDot = this.container.querySelector('#gimbal-left-dot');
    this.rightDot = this.container.querySelector('#gimbal-right-dot');
  }

  update(index) {
    if (!this.available) return;

    const [lv, lh, rv, rh] = MODES[this.mode];
    const cols = this.flight.cols;

    place(this.leftDot, cols[lh] ? cols[lh][index] : null, cols[lv] ? cols[lv][index] : null);
    place(this.rightDot, cols[rh] ? cols[rh][index] : null, cols[rv] ? cols[rv][index] : null);
  }

  destroy() {
    const left = this.container.querySelector('#gimbal-left');
    const right = this.container.querySelector('#gimbal-right');
    if (left) left.innerHTML = '';
    if (right) right.innerHTML = '';
  }
}

/**
 * Position the dot. Screen y is inverted relative to stick y: pushing the
 * stick forward should move the dot up.
 */
function place(dot, x, y) {
  if (!dot) return;

  const cx = 34 + clamp(x || 0) * 26;
  const cy = 34 - clamp(y || 0) * 26;

  dot.setAttribute('cx', cx.toFixed(1));
  dot.setAttribute('cy', cy.toFixed(1));
}

function clamp(value) {
  return Math.max(-1, Math.min(1, value === null ? 0 : value));
}

function gimbalSvg(side) {
  return `
    <svg viewBox="0 0 68 68" width="100%" height="100%" aria-hidden="true">
      <rect x="4" y="4" width="60" height="60" rx="3"
            fill="none" stroke="var(--rule-bright)" stroke-width="1" />
      <line x1="34" y1="6" x2="34" y2="62" stroke="var(--rule-bright)" stroke-width="1" />
      <line x1="6" y1="34" x2="62" y2="34" stroke="var(--rule-bright)" stroke-width="1" />
      <circle cx="34" cy="34" r="26" fill="none" stroke="var(--rule-bright)"
              stroke-width="1" stroke-dasharray="2 4" />
      <circle id="gimbal-${side}-dot" cx="34" cy="34" r="5.5"
              fill="var(--trace)" stroke="var(--ink)" stroke-width="1" />
    </svg>
  `;
}
